import type { LeagueParticipantStatsDoc } from "@/modules/leagues/types/ranking";

export interface LeagueAnalytics {
  taxaAcertoGrupo: number | null; // 0-100
  recordistaRodada: { nome: string; pontos: number; rodada: number } | null;
  reiDasCravadas: { nome: string; quantidade: number } | null;
  maisRegular: { nome: string; media: number } | null;
  rodadaMaisDificil: { rodada: number; media: number } | null;
  top5: Array<{ nome: string; pontos: number }>;
}

function mean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function stdDev(values: number[], avg: number): number {
  if (values.length < 2) return 0;
  const variance = values.reduce((sum, v) => sum + (v - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

/**
 * Toda a matemática das Estatísticas (itens 24-27), pura e sem I/O — só
 * lê o que o cron já gravou em cada participante (item 36: fonte única de
 * verdade, nada recalculado com regra paralela). Se não houver dado
 * suficiente pra alguma métrica, ela volta `null` — nunca inventamos um
 * número (item "não usar valores fictícios em produção").
 */
export function computeLeagueAnalytics(participants: LeagueParticipantStatsDoc[]): LeagueAnalytics {
  const totalAcertos = participants.reduce((sum, p) => sum + (p.acertosPlacarExato ?? 0), 0);
  const totalAvaliados = participants.reduce((sum, p) => sum + (p.totalPalpitesAvaliados ?? 0), 0);
  const taxaAcertoGrupo = totalAvaliados > 0 ? Math.round((totalAcertos / totalAvaliados) * 100) : null;

  let recordistaRodada: LeagueAnalytics["recordistaRodada"] = null;
  for (const p of participants) {
    for (const [roundStr, pontos] of Object.entries(p.pontosPorRodada ?? {})) {
      if (!recordistaRodada || pontos > recordistaRodada.pontos) {
        recordistaRodada = { nome: p.nome, pontos, rodada: Number(roundStr) };
      }
    }
  }

  let reiDasCravadas: LeagueAnalytics["reiDasCravadas"] = null;
  for (const p of participants) {
    const qtd = p.acertosPlacarExato ?? 0;
    if (qtd > 0 && (!reiDasCravadas || qtd > reiDasCravadas.quantidade)) {
      reiDasCravadas = { nome: p.nome, quantidade: qtd };
    }
  }

  // "Mais regular" = menor coeficiente de variação (desvio padrão / média)
  // entre quem já jogou pelo menos 2 rodadas — regularidade só faz sentido
  // com mais de uma amostra.
  let maisRegular: LeagueAnalytics["maisRegular"] = null;
  let bestCv = Infinity;
  for (const p of participants) {
    const values = Object.values(p.pontosPorRodada ?? {});
    if (values.length < 2) continue;
    const avg = mean(values);
    if (avg <= 0) continue;
    const cv = stdDev(values, avg) / avg;
    if (cv < bestCv) {
      bestCv = cv;
      maisRegular = { nome: p.nome, media: Math.round(avg) };
    }
  }

  // "Rodada mais difícil" = menor média de pontos entre quem jogou aquela rodada.
  const byRound = new Map<number, number[]>();
  for (const p of participants) {
    for (const [roundStr, pontos] of Object.entries(p.pontosPorRodada ?? {})) {
      const round = Number(roundStr);
      const list = byRound.get(round) ?? [];
      list.push(pontos);
      byRound.set(round, list);
    }
  }
  let rodadaMaisDificil: LeagueAnalytics["rodadaMaisDificil"] = null;
  for (const [round, values] of byRound.entries()) {
    const avg = mean(values);
    if (!rodadaMaisDificil || avg < rodadaMaisDificil.media) {
      rodadaMaisDificil = { rodada: round, media: Math.round(avg) };
    }
  }

  const top5 = [...participants]
    .sort((a, b) => (b.pontos ?? 0) - (a.pontos ?? 0))
    .slice(0, 5)
    .map((p) => ({ nome: p.nome, pontos: p.pontos ?? 0 }));

  return { taxaAcertoGrupo, recordistaRodada, reiDasCravadas, maisRegular, rodadaMaisDificil, top5 };
}
